import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, Image, Download, Settings, X, Target, Zap } from 'lucide-react'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface CompressionOptions {
  compressionType: 'quality' | 'targetSize'
  quality: number
  targetSize: number
  targetUnit: 'KB' | 'MB'
  maintainAspectRatio: boolean
}

interface ProcessedFile {
  id: string
  originalFile: File
  compressedBlob?: Blob
  originalSize: number
  compressedSize?: number
  compressionRatio?: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  iterations?: number
}

const ImageCompressor: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<CompressionOptions>({
    compressionType: 'quality',
    quality: 80,
    targetSize: 500,
    targetUnit: 'KB',
    maintainAspectRatio: true
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/')
    )

    if (imageFiles.length === 0) {
      toast.error('Please select valid image files')
      return
    }

    const newFiles: ProcessedFile[] = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      originalSize: file.size,
      status: 'pending',
      progress: 0
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${imageFiles.length} image(s) for compression`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp']
    },
    multiple: true
  })

  const compressToTargetSize = async (file: File, targetSizeBytes: number, maxIterations = 10): Promise<{ blob: Blob, iterations: number }> => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new (window as any).Image()

    return new Promise((resolve, reject) => {
      img.onload = async () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx?.drawImage(img, 0, 0)

        let quality = 0.9
        let iterations = 0
        let bestBlob: Blob | null = null

        const tryCompression = (currentQuality: number): Promise<Blob> => {
          return new Promise((res) => {
            canvas.toBlob((blob) => {
              if (blob) res(blob)
              else reject(new Error('Failed to create blob'))
            }, file.type, currentQuality)
          })
        }

        // Binary search for optimal quality
        let minQuality = 0.1
        let maxQuality = 0.9

        while (iterations < maxIterations && Math.abs(maxQuality - minQuality) > 0.05) {
          iterations++
          quality = (minQuality + maxQuality) / 2

          try {
            const blob = await tryCompression(quality)
            
            if (blob.size <= targetSizeBytes) {
              bestBlob = blob
              minQuality = quality // Try higher quality
            } else {
              maxQuality = quality // Try lower quality
            }

            // Update progress
            setFiles(prev => prev.map(f => 
              f.originalFile === file ? { 
                ...f, 
                progress: Math.min(90, (iterations / maxIterations) * 80 + 10)
              } : f
            ))

          } catch (error) {
            console.error('Compression iteration failed:', error)
            break
          }
        }

        // If we couldn't reach target size, use the best we got
        if (!bestBlob) {
          bestBlob = await tryCompression(0.1) // Minimum quality as fallback
          iterations++
        }

        resolve({ blob: bestBlob!, iterations })
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(file)
    })
  }

  const compressByQuality = async (file: File, quality: number): Promise<{ blob: Blob, iterations: number }> => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new (window as any).Image()

    return new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx?.drawImage(img, 0, 0)

        canvas.toBlob((blob) => {
          if (blob) {
            resolve({ blob, iterations: 1 })
          } else {
            reject(new Error('Compression failed'))
          }
        }, file.type, quality / 100)
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(file)
    })
  }

  const processFiles = async () => {
    const pendingFiles = files.filter(file => file.status === 'pending')
    
    for (const file of pendingFiles) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        let result: { blob: Blob, iterations: number }

        if (options.compressionType === 'targetSize') {
          const targetSizeBytes = options.targetSize * (options.targetUnit === 'MB' ? 1024 * 1024 : 1024)
          
          if (file.originalSize <= targetSizeBytes) {
            // File is already smaller than target
            result = { blob: file.originalFile as any, iterations: 0 }
          } else {
            result = await compressToTargetSize(file.originalFile, targetSizeBytes)
          }
        } else {
          result = await compressByQuality(file.originalFile, options.quality)
        }

        const compressionRatio = ((file.originalSize - result.blob.size) / file.originalSize) * 100

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { 
            ...f, 
            compressedBlob: result.blob,
            compressedSize: result.blob.size,
            compressionRatio,
            iterations: result.iterations,
            status: 'completed',
            progress: 100
          } : f
        ))

        toast.success(`Compressed ${file.originalFile.name} (${Math.round(compressionRatio)}% reduction)`)
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to compress ${file.originalFile.name}`)
      }
    }
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.compressedBlob) return

    const url = URL.createObjectURL(file.compressedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `compressed_${file.originalFile.name}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getTargetSizeBytes = () => {
    return options.targetSize * (options.targetUnit === 'MB' ? 1024 * 1024 : 1024)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Image className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            Smart Image Compressor
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Compress images to specific file sizes or quality levels with intelligent optimization algorithms.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageCompressor.header}
          className="mb-8"
        />

        {/* Compression Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <Settings className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Compression Options</h2>
          </div>
          
          {/* Compression Type Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Compression Method
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setOptions(prev => ({ ...prev, compressionType: 'quality' }))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  options.compressionType === 'quality'
                    ? 'border-[#8438ff] bg-[#8438ff]/10 text-[#8438ff]'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <Zap className="h-4 w-4" />
                <span>Quality Based</span>
              </button>
              <button
                onClick={() => setOptions(prev => ({ ...prev, compressionType: 'targetSize' }))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  options.compressionType === 'targetSize'
                    ? 'border-[#8438ff] bg-[#8438ff]/10 text-[#8438ff]'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <Target className="h-4 w-4" />
                <span>Target Size</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {options.compressionType === 'quality' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality: {options.quality}%
                </label>
                <input
                  type="range"
                  min="10"
                  max="95"
                  value={options.quality}
                  onChange={(e) => setOptions(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.quality - 10) / (95 - 10)) * 100}%, #e5e7eb ${((options.quality - 10) / (95 - 10)) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Smallest</span>
                  <span>Best Quality</span>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target File Size
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min="1"
                    max={options.targetUnit === 'MB' ? 100 : 10240}
                    value={options.targetSize}
                    onChange={(e) => setOptions(prev => ({ ...prev, targetSize: parseInt(e.target.value) || 1 }))}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent transition-all duration-200"
                    placeholder="Enter size"
                  />
                  <select
                    value={options.targetUnit}
                    onChange={(e) => setOptions(prev => ({ 
                      ...prev, 
                      targetUnit: e.target.value as 'KB' | 'MB',
                      targetSize: e.target.value === 'MB' ? Math.ceil(prev.targetSize / 1024) : prev.targetSize * 1024
                    }))}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] text-[#8438ff] font-medium bg-white hover:border-[#8438ff] transition-colors duration-200 cursor-pointer min-w-[80px]"
                  >
                    <option value="KB">KB</option>
                    <option value="MB">MB</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Target: {formatFileSize(getTargetSizeBytes())}
                </p>
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="maintainAspectRatio"
                checked={options.maintainAspectRatio}
                onChange={(e) => setOptions(prev => ({ ...prev, maintainAspectRatio: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="maintainAspectRatio" className="text-sm font-medium text-gray-700">
                Maintain Aspect Ratio
              </label>
            </div>
          </div>

          {options.compressionType === 'targetSize' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Smart Compression:</strong> The algorithm will automatically adjust quality to reach your target file size. 
                This may take multiple iterations for optimal results.
              </p>
            </div>
          )}
        </motion.div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your images here' : 'Drag & drop images here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports JPG, PNG, WebP • Max 100MB per file
          </p>
        </div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageCompressor.content}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={files.filter(f => f.status === 'pending').length === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Compress All
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Image className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-[#8438ff]">{file.originalFile.name}</p>
                        <p className="text-sm text-gray-500">
                          Original: {formatFileSize(file.originalSize)}
                          {file.compressedSize && (
                            <span className="ml-2">
                              → Compressed: {formatFileSize(file.compressedSize)}
                              {file.compressionRatio && (
                                <span className="text-green-600 ml-1">
                                  ({Math.round(file.compressionRatio)}% reduction)
                                </span>
                              )}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && file.compressedBlob && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {(file.status === 'processing' || file.status === 'completed') && (
                    <div className="progress-bar mb-2">
                      <div 
                        className="progress-fill"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center justify-between text-sm">
                    <span className={`
                      ${file.status === 'pending' ? 'text-gray-500' : ''}
                      ${file.status === 'processing' ? 'text-blue-600' : ''}
                      ${file.status === 'completed' ? 'text-green-600' : ''}
                      ${file.status === 'error' ? 'text-red-600' : ''}
                    `}>
                      {file.status === 'pending' && 'Ready to compress'}
                      {file.status === 'processing' && `Compressing... ${Math.round(file.progress)}%`}
                      {file.status === 'completed' && `Compressed successfully${file.iterations ? ` (${file.iterations} iterations)` : ''}`}
                      {file.status === 'error' && 'Compression failed'}
                    </span>
                    
                    {file.status === 'completed' && options.compressionType === 'targetSize' && file.compressedSize && (
                      <span className={`text-xs ${
                        file.compressedSize <= getTargetSizeBytes() ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {file.compressedSize <= getTargetSizeBytes() ? '✓ Target reached' : '⚠ Close to target'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageCompressor.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default ImageCompressor
